import {
	faClipboardCheck,
	faClipboardList,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type {
	PortalOrderListItemDto,
	PortalPendingApprovalItemDto,
} from '@supply-mover-2-0/shared';
import { getOrderStatusLabel, OrderStatus } from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AccountBreadcrumb } from '../components/AccountBreadcrumb';
import { CardChevron } from '../components/CardChevron';
import { EmptyState } from '../components/EmptyState';
import {
	HistoryFilters,
	historyRangeParamUpdates,
	resolveHistoryRange,
} from '../components/HistoryFilterBar';
import { MobilePageHeader } from '../components/layout/MobilePageHeader';
import { Paginator } from '../components/Paginator';
import { HistoryListSkeleton } from '../components/skeletons';
import { useDisplaySettings } from '../hooks/useDisplaySettings';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { appScrollToTop } from '../lib/appScroll';
import { formatCurrency } from '../lib/formatCurrency';
import * as orderService from '../services/portalOrderService';
import { useLoginModalStore } from '../stores/loginModalStore';

const PAGE_LIMIT = 20;

/** Map order status code to a color scheme for the badge. */
function statusBadgeClasses(status: string): string {
	switch (status) {
		case OrderStatus.SHIPPED:
		case OrderStatus.PARTIALLY_SHIPPED:
		case OrderStatus.APPROVED:
		case OrderStatus.OVERRIDE_APPROVED:
			return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300';
		case OrderStatus.CANCELLED:
		case OrderStatus.OVERRIDE_REJECTED:
			return 'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300';
		case OrderStatus.PENDING:
		case OrderStatus.SUBMITTED:
		case OrderStatus.SENT_TO_ERP:
			return 'bg-portal-accent/15 text-portal-accent';
		case OrderStatus.APPROVAL_REQUIRED:
		case OrderStatus.HELD:
		case OrderStatus.CANCELLATION_REQUESTED:
			return 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300';
		default:
			return 'bg-brand-input text-gray-700 dark:text-gray-200';
	}
}

/**
 * Order history page with date/client filters, pending approval tab, and pagination.
 * Shows orders across all assigned clients.
 */
export function OrdersPage(): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { token, isAuthenticated, availableClients } = usePortalAuth();
	const display = useDisplaySettings();
	const openLoginModal = useLoginModalStore((s) => s.open);
	const [searchParams, setSearchParams] = useSearchParams();

	const currentPage = parseInt(searchParams.get('page') ?? '1', 10);
	const searchQuery = searchParams.get('q') ?? '';
	// Relative range key ('' = the default, last 3 months) resolved to
	// concrete ISO bounds for the API (PD-14674). 'custom' carries explicit
	// from/to bounds (PD-14899).
	const rangeParam = searchParams.get('range') ?? '';
	const fromParam = searchParams.get('from') ?? '';
	const toParam = searchParams.get('to') ?? '';
	const { dateFrom, dateTo } = resolveHistoryRange(
		rangeParam,
		fromParam,
		toParam,
	);
	const clientIdParam = searchParams.get('clientId') ?? '';
	const activeTab = searchParams.get('tab') ?? 'history';

	const [searchInput, setSearchInput] = useState(searchQuery);

	// Order history query
	const { data: historyData, isLoading: historyLoading } = useQuery({
		queryKey: [
			'orders',
			'history',
			{
				page: currentPage,
				search: searchQuery,
				dateFrom,
				dateTo,
				clientId: clientIdParam,
			},
		],
		queryFn: () =>
			orderService.getOrders(token ?? '', {
				page: currentPage,
				limit: PAGE_LIMIT,
				search: searchQuery.length > 0 ? searchQuery : undefined,
				dateFrom,
				dateTo,
				clientId:
					clientIdParam.length > 0 ? parseInt(clientIdParam, 10) : undefined,
			}),
		enabled: token != null && activeTab === 'history',
		staleTime: 30_000,
	});

	// Pending approval query
	const { data: approvalData, isLoading: approvalLoading } = useQuery({
		queryKey: [
			'orders',
			'pending-approval',
			{
				page: currentPage,
				search: searchQuery,
				dateFrom,
				dateTo,
				clientId: clientIdParam,
			},
		],
		queryFn: () =>
			orderService.getPendingApproval(token ?? '', {
				page: currentPage,
				limit: PAGE_LIMIT,
				search: searchQuery.length > 0 ? searchQuery : undefined,
				clientId:
					clientIdParam.length > 0 ? parseInt(clientIdParam, 10) : undefined,
				dateFrom,
				dateTo,
			}),
		enabled: token != null && activeTab === 'approval',
		staleTime: 30_000,
	});

	const orders = historyData?.data ?? [];
	const pendingOrders = approvalData?.data ?? [];
	const pagination =
		activeTab === 'history'
			? (historyData?.pagination ?? null)
			: (approvalData?.pagination ?? null);
	const isLoading = activeTab === 'history' ? historyLoading : approvalLoading;

	const updateParams = useCallback(
		(updates: Record<string, string | null>) => {
			setSearchParams((prev) => {
				const next = new URLSearchParams(prev);
				for (const [key, value] of Object.entries(updates)) {
					if (value == null || value.length === 0) {
						next.delete(key);
					} else {
						next.set(key, value);
					}
				}
				return next;
			});
		},
		[setSearchParams],
	);

	const handleSearchSubmit = useCallback(() => {
		updateParams({
			q: searchInput.trim().length > 0 ? searchInput.trim() : null,
			page: null,
		});
	}, [searchInput, updateParams]);

	// Commit the whole filter tray at once (PD-14701).
	const handleApplyFilters = useCallback(
		(next: Record<string, string>) => {
			const clientId = next.clientId;
			updateParams({
				...historyRangeParamUpdates(next),
				clientId: clientId.length > 0 ? clientId : null,
				page: null,
			});
		},
		[updateParams],
	);

	const handleTabChange = useCallback(
		(tab: string) => {
			updateParams({ tab: tab === 'history' ? null : tab, page: null });
		},
		[updateParams],
	);

	const handlePageChange = useCallback(
		(page: number) => {
			updateParams({ page: page > 1 ? String(page) : null });
			appScrollToTop('smooth');
		},
		[updateParams],
	);

	// Prompt login if not authenticated — useEffect to avoid setState during render
	useEffect(() => {
		if (!isAuthenticated) {
			openLoginModal();
		}
	}, [isAuthenticated, openLoginModal]);

	if (!isAuthenticated) {
		return <div />;
	}

	return (
		<div className="px-4 py-4 max-w-4xl mx-auto">
			<MobilePageHeader title="Orders" backPath="/account" />
			<AccountBreadcrumb current="Orders" />
			<h1 className="hidden md:block text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
				{t('Orders')}
			</h1>

			{/* Search + one filter tray (date range / location), shared across
			    both tabs, with Apply-Cancel (PD-14701). */}
			<div className="mb-4">
				<HistoryFilters
					searchValue={searchInput}
					onSearchChange={setSearchInput}
					onSearchSubmit={handleSearchSubmit}
					searchPlaceholder={t('Search by order #, PO #, or item...')}
					fields={[
						{ kind: 'range', key: 'range', label: 'Date Range' },
						...(availableClients.length > 1
							? [
									{
										kind: 'location' as const,
										key: 'clientId' as const,
										label: 'Location',
										clients: availableClients,
									},
								]
							: []),
					]}
					values={{
						range: rangeParam,
						from: fromParam,
						to: toParam,
						clientId: clientIdParam,
					}}
					onApply={handleApplyFilters}
				/>
			</div>

			{/* Tabs: History / Pending Approval */}
			<div className="flex gap-1 mb-4 border-b border-brand-border">
				<button
					type="button"
					onClick={() => {
						handleTabChange('history');
					}}
					className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
						activeTab === 'history'
							? 'border-portal-accent text-portal-accent'
							: 'border-transparent text-brand-muted hover:text-gray-700'
					}`}
				>
					<FontAwesomeIcon icon={faClipboardList} className="text-xs" />
					{t('Order History')}
				</button>
				<button
					type="button"
					onClick={() => {
						handleTabChange('approval');
					}}
					className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
						activeTab === 'approval'
							? 'border-portal-accent text-portal-accent'
							: 'border-transparent text-brand-muted hover:text-gray-700'
					}`}
				>
					<FontAwesomeIcon icon={faClipboardCheck} className="text-xs" />
					{t('Awaiting Approval')}
				</button>
			</div>

			{/* Loading */}
			{/* Loading — skeleton cards shaped like the list (PD-14692) */}
			{isLoading && <HistoryListSkeleton />}

			{/* Order History list */}
			{!isLoading && activeTab === 'history' && orders.length > 0 && (
				<div className="space-y-2 mb-6">
					{orders.map((order: PortalOrderListItemDto) => (
						<button
							key={order.id}
							type="button"
							onClick={() => navigate(`/orders/${order.id}`)}
							className="w-full text-left bg-brand-surface border border-brand-border rounded-xl p-4 hover:shadow-sm transition-shadow"
						>
							<div className="flex items-start justify-between gap-3">
								{/* flex-1 pins the total + chevron to the card's right
								    edge; without it justify-between floats the total
								    mid-card at a width that varies per row (PD-14241). */}
								<div className="flex-1 min-w-0">
									<div className="flex items-center gap-2 mb-1 flex-wrap">
										<span className="text-sm font-medium text-gray-900 dark:text-gray-100">
											{t('Order')} #
											{t(
												order.externalId2 ??
													order.externalId ??
													String(order.id),
											)}
										</span>
										<span
											className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusBadgeClasses(order.status ?? '')}`}
										>
											{t(getOrderStatusLabel(order.status))}
										</span>
										{/* Subtle "Promo" pill on rows where the customer
										    saved money. Detail page has the named breakdown;
										    here we just want the at-a-glance signal so the
										    customer can scroll their history and quickly
										    spot the discounted orders. */}
										{order.hasPromo && (
											<span className="text-[10px] font-semibold uppercase tracking-wide bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 rounded-full px-2 py-0.5">
												{t('Promo')}
											</span>
										)}
									</div>
									{order.clientName != null && (
										<p className="text-xs text-brand-muted">
											{t(order.clientName)}
										</p>
									)}
									<p className="text-xs text-brand-muted mt-0.5">
										{t(
											order.date != null
												? new Date(order.date).toLocaleDateString()
												: '-',
										)}
										{order.customerPO != null && (
											<>
												{' · '}
												{t('PO')}: {t(order.customerPO)}
											</>
										)}
									</p>
								</div>
								<div className="text-right shrink-0">
									{/* PD-14239: hide the order total when this portal user
									    cannot see pricing; keep the item count. */}
									{display.canSeePricing && (
										<div className="text-sm font-bold text-gray-900 dark:text-gray-100">
											{t(formatCurrency(order.total) ?? '-')}
										</div>
									)}
									{order.numberOfLines != null && (
										<div className="text-xs text-gray-400 mt-0.5">
											{t(String(order.numberOfLines))} {t('items')}
										</div>
									)}
								</div>
								<CardChevron />
							</div>
						</button>
					))}
				</div>
			)}

			{/* Pending Approval list */}
			{!isLoading && activeTab === 'approval' && pendingOrders.length > 0 && (
				<div className="space-y-2 mb-6">
					{pendingOrders.map((order: PortalPendingApprovalItemDto) => (
						<button
							key={order.id}
							type="button"
							onClick={() => navigate(`/orders/${order.id}`)}
							className="w-full text-left bg-brand-surface border border-amber-200 dark:border-amber-800 rounded-xl p-4 hover:shadow-sm transition-shadow"
						>
							<div className="flex items-start justify-between gap-3">
								{/* flex-1 pins the total + chevron to the card's right
								    edge; without it justify-between floats the total
								    mid-card at a width that varies per row (PD-14241). */}
								<div className="flex-1 min-w-0">
									<div className="flex items-center gap-2 mb-1">
										<span className="text-sm font-medium text-gray-900 dark:text-gray-100">
											{t('Order')} #
											{t(
												order.externalId2 ??
													order.externalId ??
													String(order.id),
											)}
										</span>
										<span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300">
											{t('Awaiting Approval')}
										</span>
									</div>
									{order.clientName != null && (
										<p className="text-xs text-brand-muted">
											{t(order.clientName)}
										</p>
									)}
									{order.submitterName != null && (
										<p className="text-xs text-brand-muted mt-0.5">
											{t('Submitted by')}: {t(order.submitterName)}
										</p>
									)}
								</div>
								<div className="text-right shrink-0">
									{/* PD-14239: hide the order total when this portal user
									    cannot see pricing; keep the item count. */}
									{display.canSeePricing && (
										<div className="text-sm font-bold text-gray-900 dark:text-gray-100">
											{t(formatCurrency(order.total) ?? '-')}
										</div>
									)}
									{order.numberOfLines != null && (
										<div className="text-xs text-gray-400 mt-0.5">
											{t(String(order.numberOfLines))} {t('items')}
										</div>
									)}
								</div>
								<CardChevron />
							</div>
						</button>
					))}
				</div>
			)}

			{/* Empty states (PD-14699) — filtered-empty variants offer the way
			    out before the truly-empty message. */}
			{!isLoading &&
				activeTab === 'history' &&
				orders.length === 0 &&
				(searchQuery.length > 0 || clientIdParam.length > 0 ? (
					<EmptyState
						icon={faClipboardList}
						title="No orders match your filters"
						hint="Try removing a filter to see more results."
						actionLabel="Clear filters"
						onAction={() => {
							setSearchInput('');
							updateParams({ q: null, clientId: null, page: null });
						}}
					/>
				) : rangeParam !== 'all' ? (
					<EmptyState
						icon={faClipboardList}
						title="No orders in this date range"
						hint="Older orders may fall outside the selected range."
						actionLabel="View all time"
						onAction={() => {
							updateParams({ range: 'all', from: null, to: null, page: null });
						}}
					/>
				) : (
					<EmptyState
						icon={faClipboardList}
						title="No orders yet"
						hint="Your orders will appear here once you have placed one."
					/>
				))}

			{!isLoading && activeTab === 'approval' && pendingOrders.length === 0 && (
				<EmptyState
					icon={faClipboardCheck}
					title="No orders awaiting your approval"
					hint="Orders that need your sign-off will appear here."
				/>
			)}

			{/* Pagination */}
			{pagination != null && (
				<Paginator
					currentPage={currentPage}
					totalPages={pagination.totalPages}
					onPageChange={handlePageChange}
				/>
			)}
		</div>
	);
}
